import React, { CSSProperties, useEffect, useState, useCallback } from "react";
import axios from "axios";
import _ from "lodash";

import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
  Grid,
  Stack,
  Card,
  CardContent,
  CardHeader,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  CardActionArea,
  Tooltip,
  Switch,
  FormGroup,
  FormControlLabel,
} from "@mui/material";

import moment from "moment";

import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faUser } from "@fortawesome/free-solid-svg-icons";

import { useAppDispatch } from "../hooks/redux";
import PermissionGuard from "../components/PermissionGuard";
import { openModal } from "../features/modals";

import { useParams } from "react-router";

import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import GridOnIcon from "@mui/icons-material/GridOn";

import MODALS from "../constants/modals";
import { LoadingButton } from "@mui/lab";

import * as pdfMake from "pdfmake/build/pdfmake";
import * as pdfFonts from "pdfmake/build/vfs_fonts";
import writeXlsxFile from "write-excel-file";
import type { SheetData, Row } from "write-excel-file";
import PERMISSIONS from "../constants/permissions";
import {
  ChairScheduleResponse,
  ClinicChairResponse,
  IWorkloadResponse,
} from "../types/clinic";
import {
  ContentTable,
  TDocumentDefinitions,
  TableCell,
  Content,
} from "pdfmake/interfaces";
import { IHolidayResponse } from "../types/holiday";

import CircleIcon from "@mui/icons-material/Circle";

import { CHAIR_TYPES, ChairTypes } from "../constants/chairTypes";
import { HolidayTypes } from "../constants/holidayTypes";
import ScheduleItem from "../components/ScheduleItem";

// @ts-ignore
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const Schedule: React.FunctionComponent<{}> = () => {
  const { clinic } = useParams();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [date, setDate] = useState<Date>(moment().toDate());
  const [chairs, setChairs] = useState<ClinicChairResponse[]>([]);
  const [visibleMonth, setVisibleMonth] = useState<Date>(moment().toDate());
  const [workload, setWorkload] = useState<IWorkloadResponse>();
  const [holidays, setHolidays] = useState<IHolidayResponse[]>([]);

  const [isEditable, setIsEditable] = useState(false);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  const loadData = useCallback(() => {
    setIsLoading(true);
    axios
      .get<ClinicChairResponse[]>(`/clinics/${clinic}/chairs/schedule`, {
        params: {
          date,
        },
      })
      .then((response) => {
        setChairs(response.data);
      })
      .catch((error) => {
        console.log("error:", error);
      })
      .finally(() => {
        setIsLoading(true);
      });
  }, [date, clinic]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const reorderChairs = useCallback(
    (order: string[]) => {
      axios
        .post("/classifiers/chairs/reorder", {
          clinic,
          order,
        })
        .catch((error) => {
          console.log("error:", error);
        });
    },
    [clinic]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        setChairs((chairs) => {
          const oldIndex = chairs.findIndex((chair) => chair._id === active.id);
          const newIndex = chairs.findIndex((chair) => chair._id === over.id);

          if (oldIndex === -1 || newIndex === -1) {
            return chairs;
          }

          const reordered = arrayMove(chairs, oldIndex, newIndex);

          reorderChairs(reordered.map((chair) => chair._id));

          return reordered;
        });
      }
    },
    [reorderChairs]
  );

  const loadWorkload = useCallback(() => {
    axios
      .get<IWorkloadResponse>(`/clinics/${clinic}/workload`, {
        params: {
          date: moment.utc(visibleMonth).format(),
        },
      })
      .then((response) => {
        setWorkload(response.data);
      })
      .catch((err) => {
        console.log("error:", err);
      });
  }, [visibleMonth]);

  useEffect(() => {
    loadWorkload();
  }, [loadWorkload]);

  const loadHolidays = useCallback(() => {
    axios
      .get<IHolidayResponse[]>(`/clinics/${clinic}/holidays`, {
        params: {
          date: moment(visibleMonth).format(),
        },
      })
      .then((response) => {
        setHolidays(response.data);
      })
      .catch((err) => {
        console.log("error:", err);
      });
  }, [visibleMonth]);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  const exportPdf = useCallback(async () => {
    if (!workload) {
      return;
    }

    setIsExporting(true);

    let docDefinition: TDocumentDefinitions = {
      content: [
        {
          text: `${workload.clinic.name} (${moment(visibleMonth)
            .startOf("month")
            .format("L")} - ${moment(visibleMonth)
            .endOf("month")
            .format("L")})`,
          style: "header",
        },
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 10],
        },
        headerChair: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 10],
          alignment: "center",
        },
        tableExample: {
          margin: [0, 5, 0, 15],
        },
      },
    };
    let contentTable: ContentTable = {
      style: "tableExample",
      table: {
        widths: [90, ...workload.chairs.map((chair) => "*")],
        body: [],
      },
    };
    let tableHeaders: TableCell[] = [{ text: "Datums", style: "tableHeader" }];
    workload.chairs.forEach((chair) => {
      tableHeaders.push({ text: chair.name, style: "headerChair" });
    });
    contentTable.table.body.push(tableHeaders);

    workload.schedule.forEach((date) => {
      let row: TableCell[] = [moment(date.date).format("L")];
      workload.chairs.forEach((chair) => {
        const shifts = date.chairs.filter(
          (scheduleChair) => scheduleChair.chair === chair._id
        );

        if (shifts.length > 0) {
          let shiftsText = "";
          shifts.forEach((shift) => {
            if (shifts.length > 1 || shift.end) {
              shiftsText += `${moment(shift.date).format("LT")} - ${
                shift.end ? moment(shift.end).format("LT") : ""
              }\n`;
            }
            switch (shift.type) {
              case ChairTypes.Dentist:
                shiftsText += `${shift.doctor ? shift.doctor.name : ""} ${
                  shift.assistant ? `\n${shift.assistant.name}` : ""
                }\n`;
                break;
              case ChairTypes.Orthodontist:
                shiftsText += `${
                  shift.orthodontist ? shift.orthodontist.name : ""
                } ${shift.assistant ? `\n${shift.assistant.name}` : ""}\n`;
                break;
              case ChairTypes.Bus:
                shiftsText = `${
                  shift.specialist ? shift.specialist.name : ""
                } ${shift.assistant ? `\n${shift.assistant.name}` : ""} ${
                  shift.driver ? `\n${shift.driver.name}` : ""
                } ${shift.destination ? `\n${shift.destination}` : ""}\n`;
                break;
              case ChairTypes.Hygienist:
                shiftsText += `${shift.hygenist ? shift.hygenist.name : ""}\n`;
                break;
              case ChairTypes.Surgeon:
                shiftsText += `${shift.doctor ? shift.doctor.name : ""} ${
                  shift.assistant ? `\n${shift.assistant.name}` : ""
                } ${
                  shift.extraAssistant ? `\n${shift.extraAssistant.name}` : ""
                }\n`;
                break;
              default:
            }
          });
          row.push(shiftsText);
        } else {
          row.push("");
        }
      });
      contentTable.table.body.push(row);
    });
    (docDefinition.content as Content[]).push(contentTable);
    pdfMake.createPdf(docDefinition).open();

    setIsExporting(false);
  }, [workload]);

  const exportXlsx = useCallback(async () => {
    if (!workload) {
      return;
    }

    setIsExporting(true);

    let tableHeaders: Row = [{ value: "Datums", fontWeight: "bold" }];
    const columns = [{ width: 20 }];
    const defaultFontSize = 12;

    workload.chairs.forEach((chair) => {
      tableHeaders.push({ value: chair.name, fontWeight: "bold" });
      columns.push({ width: 20 });
    });

    const data: SheetData = [tableHeaders];

    workload.schedule.forEach((date) => {
      let row: Row = [
        { value: moment(date.date).format("L"), fontWeight: "bold" },
      ];
      workload.chairs.forEach((chair) => {
        const shifts = date.chairs.filter(
          (scheduleChair) => scheduleChair.chair === chair._id
        );

        if (shifts.length > 0) {
          let shiftsText = "";
          shifts.forEach((shift) => {
            if (shifts.length > 1 || shift.end) {
              shiftsText += `${moment(shift.date).format("LT")} - ${
                shift.end ? moment(shift.end).format("LT") : ""
              }\n`;
            }
            switch (shift.type) {
              case ChairTypes.Dentist:
                shiftsText += `${shift.doctor ? shift.doctor.name : ""} ${
                  shift.assistant ? `\n${shift.assistant.name}` : ""
                }\n`;
                break;
              case ChairTypes.Orthodontist:
                shiftsText += `${
                  shift.orthodontist ? shift.orthodontist.name : ""
                } ${shift.assistant ? `\n${shift.assistant.name}` : ""}\n`;
                break;
              case ChairTypes.Bus:
                shiftsText = `${
                  shift.specialist ? shift.specialist.name : ""
                } ${shift.assistant ? `\n${shift.assistant.name}` : ""} ${
                  shift.driver ? `\n${shift.driver.name}` : ""
                } ${shift.destination ? `\n${shift.destination}` : ""}\n`;
                break;
              case ChairTypes.Hygienist:
                shiftsText += `${shift.hygenist ? shift.hygenist.name : ""}\n`;
                break;
              case ChairTypes.Surgeon:
                shiftsText += `${shift.doctor ? shift.doctor.name : ""} ${
                  shift.assistant ? `\n${shift.assistant.name}` : ""
                } ${
                  shift.extraAssistant ? `\n${shift.extraAssistant.name}` : ""
                }\n`;
                break;
              default:
            }
          });
          row.push({ value: shiftsText });
        } else {
          row.push({ value: "" });
        }
      });
      row.forEach((p) => {
        const lines = p?.value?.toString().split("\n").length || 1;

        p!.wrap = true;
        p!.height = defaultFontSize * lines * 1.4;
        p!.alignVertical = "top";
      });
      data.push(row);
    });

    const sheetName = workload.clinic.name.replace(/[\[\]\/\\\:\*\?]/g, "");
    const fileName = `${workload.clinic.name}_${moment(visibleMonth)
      .startOf("month")
      .format("L")}-${moment(visibleMonth).endOf("month").format("L")}`.slice(
      0,
      -1
    );

    await writeXlsxFile(data, {
      columns,
      fileName: fileName + ".xlsx",
      sheet: sheetName,
    });

    setIsExporting(false);
  }, [workload]);

  if (!clinic) {
    // TODO: show error
    return <>error?</>;
  }

  const open = Boolean(menuAnchorEl);

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const handleExportClose = () => {
    setMenuAnchorEl(null);
  };

  return (
    <Stack spacing={2}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h4">Grafiks</Typography>
        </Grid>
        <Grid item>
          <LoadingButton
            loading={isExporting}
            startIcon={<FileDownloadIcon />}
            aria-controls={open ? "export-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-label="export as"
            aria-haspopup="menu"
            onClick={handleExportClick}
          >
            Eksportēt
          </LoadingButton>
          <Menu
            id="export-menu"
            anchorEl={menuAnchorEl}
            open={open}
            onClose={handleExportClose}
          >
            <MenuItem
              onClick={() => {
                exportPdf();
                setMenuAnchorEl(null);
              }}
            >
              <ListItemIcon>
                <PictureAsPdfIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="pdf"></ListItemText>
            </MenuItem>
            <MenuItem
              onClick={() => {
                exportXlsx();
                setMenuAnchorEl(null);
              }}
            >
              <ListItemIcon>
                <GridOnIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="xlsx"></ListItemText>
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>

      <Stack>
        <StaticDatePicker
          sx={{
            padding: 2,
            "& .MuiPickersSlideTransition-root": {
              minHeight: 334,
            },
            " .MuiDateCalendar-root": {
              height: "auto",
            },
          }}
          className="picker-with-dots"
          onChange={(newValue) => {
            if (newValue) {
              setDate(newValue.toDate());
            }
          }}
          value={moment(date)}
          onMonthChange={(month) => {
            setVisibleMonth(
              moment(month).utc().add(moment(month).utcOffset(), "m").toDate()
            );
          }}
          shouldDisableDate={(day) => {
            const tmpHoliday = holidays.find((holiday) =>
              moment(day).isBetween(
                moment(holiday.from).startOf("day"),
                moment(holiday.to).endOf("day"),
                undefined,
                "[]"
              )
            );

            if (tmpHoliday && tmpHoliday.type === HolidayTypes.holiday) {
              return true;
            }

            if (
              moment(day).weekday() === 6 &&
              (!tmpHoliday || tmpHoliday.type !== HolidayTypes.workday)
            ) {
              return true;
            }

            return false;
          }}
          displayStaticWrapperAs="desktop"
          slots={{
            day: (props) => {
              const d = workload?.schedule.find((f) =>
                moment(props.day).isSame(
                  moment.utc(f._id, "YYYY-MM-DD").local(),
                  "day"
                )
              );

              const missingSchedule =
                workload?.chairs.length !== d?.chairs.length;

              let missingDoctor = false;
              let missingAssistant = false;
              let missingHygenist = false;
              let missingDestination = false;
              let missingOrthodontist = false;
              let missingDriver = false;
              let missingSanitar = false;

              if (d) {
                for (let chair of d.chairs) {
                  switch (chair.type) {
                    case ChairTypes.Bus:
                      if (_.isEmpty(chair.specialist)) {
                        missingDoctor = true;
                      }
                      if (_.isEmpty(chair.destination)) {
                        missingDestination = true;
                      }
                      if (_.isEmpty(chair.assistant)) {
                        missingAssistant = true;
                      }
                      if (_.isEmpty(chair.driver)) {
                        missingDriver = true;
                      }

                      break;
                    case ChairTypes.Hygienist:
                      if (_.isEmpty(chair.hygenist)) {
                        missingHygenist = true;
                      }
                      break;
                    case ChairTypes.Sanitar:
                      if (_.isEmpty(chair.sanitar)) {
                        missingSanitar = true;
                      }
                      break;
                    case ChairTypes.Orthodontist:
                      if (_.isEmpty(chair.orthodontist)) {
                        missingOrthodontist = true;
                      }

                      break;
                    case ChairTypes.Dentist:
                    case ChairTypes.Surgeon:
                    default:
                      if (_.isEmpty(chair.doctor)) {
                        missingDoctor = true;
                      }
                      if (_.isEmpty(chair.assistant)) {
                        missingAssistant = true;
                      }
                      break;
                  }
                }
              }

              return (
                <Stack key={props.key} sx={{ paddingBottom: 1 }}>
                  <PickersDay {...props} />
                  {!props.disabled &&
                  workload &&
                  moment(props.day).isSame(moment(visibleMonth), "month") ? (
                    <Stack direction="row" justifyContent="center">
                      {missingSchedule ? (
                        <CircleIcon color="success" sx={{ fontSize: 7 }} />
                      ) : null}
                      {missingDoctor ? (
                        <CircleIcon color="warning" sx={{ fontSize: 7 }} />
                      ) : null}
                      {missingAssistant ? (
                        <CircleIcon color="secondary" sx={{ fontSize: 7 }} />
                      ) : null}
                      {missingHygenist ? (
                        <CircleIcon color="info" sx={{ fontSize: 7 }} />
                      ) : null}
                      {missingOrthodontist ? (
                        <CircleIcon color="error" sx={{ fontSize: 7 }} />
                      ) : null}
                      {missingDriver ? (
                        <CircleIcon color="action" sx={{ fontSize: 7 }} />
                      ) : null}
                      {missingSanitar ? (
                        <CircleIcon color="primary" sx={{ fontSize: 7 }} />
                      ) : null}
                    </Stack>
                  ) : null}
                </Stack>
              );
            },
          }}
        />
        <Stack direction="row" spacing={2} justifyContent="right">
          <Typography variant="caption">
            <CircleIcon color="success" sx={{ fontSize: 10 }} /> brīva iekārta
          </Typography>
          <Typography variant="caption">
            <CircleIcon color="warning" sx={{ fontSize: 10 }} /> trūkst ārsts
          </Typography>
          <Typography variant="caption">
            <CircleIcon color="secondary" sx={{ fontSize: 10 }} /> trūkst
            asistents
          </Typography>
          <Typography variant="caption">
            <CircleIcon color="info" sx={{ fontSize: 10 }} /> trūkst higiēnists
          </Typography>
          <Typography variant="caption">
            <CircleIcon color="error" sx={{ fontSize: 10 }} /> trūkst ortodonts
          </Typography>
          <Typography variant="caption">
            <CircleIcon color="action" sx={{ fontSize: 10 }} /> trūkst šoferis
          </Typography>
          <Typography variant="caption">
            <CircleIcon color="primary" sx={{ fontSize: 10 }} /> trūkst sanitārs
          </Typography>
        </Stack>
      </Stack>
      <Stack spacing={2}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4">Krēsli</Typography>
          </Grid>
          <PermissionGuard permission={PERMISSIONS.WRITE_CHAIRS}>
            {({ hasAccess }) => (
              <>
                {hasAccess ? (
                  <Grid item>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isEditable}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              setIsEditable(event.target.checked);
                              if (!event.target.checked) loadData();
                            }}
                          />
                        }
                        label={
                          <Typography variant="button" color="primary">
                            KĀRTOT
                          </Typography>
                        }
                      />
                    </FormGroup>
                  </Grid>
                ) : null}
              </>
            )}
          </PermissionGuard>
        </Grid>

        <PermissionGuard permission={PERMISSIONS.WRITE_SCHEDULE}>
          {({ hasAccess }) => {
            const chairCards = chairs.map((chair) => (
              <Chair
                key={chair._id}
                disabled={
                  !hasAccess ||
                  holidays.find(
                    (holiday) =>
                      moment(date).isBetween(
                        moment(holiday.from).startOf("day"),
                        moment(holiday.to).endOf("day"),
                        undefined,
                        "[]"
                      ) && holiday.type === HolidayTypes.holiday
                  ) !== undefined
                }
                isEditable={isEditable}
                date={date}
                chair={chair}
                clinic={clinic}
                callback={(newValue) => {
                  loadData();
                  loadWorkload();
                }}
              />
            ));

            return (
              <div>
                {isEditable ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={chairs.map((chair) => chair._id)}
                      strategy={rectSortingStrategy}
                    >
                      <Grid container spacing={2}>
                        {chairCards}
                      </Grid>
                    </SortableContext>
                  </DndContext>
                ) : (
                  <Grid container spacing={2}>
                    {chairCards}
                  </Grid>
                )}
              </div>
            );
          }}
        </PermissionGuard>
      </Stack>
    </Stack>
  );
};

interface IChairProps {
  clinic: string;
  date: Date;
  chair: ClinicChairResponse;
  callback: (newValue?: ChairScheduleResponse) => void;
  disabled?: boolean;
  isEditable?: boolean;
}

const Chair: React.FunctionComponent<IChairProps> = ({
  date,
  chair,
  clinic,
  disabled,
  isEditable,
  callback,
}) => {
  const dispatch = useAppDispatch();

  const {
    isDragging,
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: chair._id });

  const inlineStyles: CSSProperties = {
    opacity: isDragging ? "0.7" : "1",
    transformOrigin: "50% 50%",
    cursor: isDragging ? "grabbing" : "grab",
    boxShadow: isDragging
      ? "rgb(63 63 68 / 5%) 0px 2px 0px 2px, rgb(34 33 81 / 15%) 0px 2px 3px 2px"
      : undefined,
    // Translate (not Transform) so a card keeps its own size while it swaps
    // places with a taller or shorter one
    transform: CSS.Translate.toString(transform),
    transition: transition || undefined,
  };

  const cardSortableProps = {
    ref: setNodeRef,
    style: inlineStyles,
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        {...(isEditable
          ? { ...cardSortableProps, ...attributes, ...listeners }
          : {})}
      >
        <CardHeader
          title={chair.name}
          action={
            <PermissionGuard permission={PERMISSIONS.WRITE_SCHEDULE}>
              {({ hasAccess }) => (
                <>
                  {hasAccess && !isEditable ? (
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault();
                        let startDate = moment(date)
                          .set({ hour: 9, minute: 0 })
                          .toDate();

                        if (chair.schedule?.length) {
                          const lastSchedule =
                            chair.schedule[chair.schedule.length - 1];

                          if (lastSchedule.end) {
                            startDate = moment(lastSchedule.end).toDate();
                          }
                        }

                        dispatch(
                          openModal({
                            type: MODALS.Schedule,
                            clinicId: clinic,
                            date: startDate,
                            chairId: chair._id,
                            callback: () => callback(),
                          })
                        );
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  ) : null}
                </>
              )}
            </PermissionGuard>
          }
        />
        <CardContent>
          <Stack spacing={1} divider={<Divider />}>
            {chair.schedule?.map((schedule) => (
              <Tooltip title={CHAIR_TYPES[schedule.type].label}>
                <CardActionArea
                  key={`${schedule._id}-${moment(schedule.updatedAt).unix()}`}
                  sx={{ p: 1 }}
                  disabled={disabled || isEditable}
                  onClick={() => {
                    dispatch(
                      openModal({
                        type: MODALS.Schedule,
                        clinicId: clinic,
                        date: date,
                        chairId: chair._id,
                        initialValues: schedule,
                        callback: () => callback(),
                      })
                    );
                  }}
                >
                  <Stack direction="row" spacing={2}>
                    <Stack alignItems="center">
                      <Typography variant="caption">
                        {schedule.end && moment(schedule.date).format("LT")}
                      </Typography>
                      <Divider orientation="vertical" flexItem sx={{ flex: 1 }}>
                        {CHAIR_TYPES[schedule.type].icon}
                      </Divider>
                      {schedule.end && (
                        <Typography variant="caption">
                          {moment(schedule.end).format("LT")}
                        </Typography>
                      )}
                    </Stack>
                    <Stack spacing={2} flex={1}>
                      {[ChairTypes.Dentist, ChairTypes.Surgeon].includes(
                        schedule.type
                      ) && (
                        <ScheduleItem
                          label="Dr."
                          value={schedule.doctor?.name || ""}
                          icon={<FontAwesomeIcon icon={faUser} fontSize={16} />}
                        />
                      )}
                      {schedule.type === ChairTypes.Orthodontist && (
                        <ScheduleItem
                          label="Ortodonts"
                          value={schedule.orthodontist?.name}
                          icon={<FontAwesomeIcon icon={faUser} />}
                        />
                      )}
                      {[
                        ChairTypes.Dentist,
                        ChairTypes.Surgeon,
                        ChairTypes.Orthodontist,
                      ].includes(schedule.type) && (
                        <ScheduleItem
                          label="Asistents"
                          value={schedule.assistant?.name}
                          icon={<FontAwesomeIcon icon={faUser} />}
                        />
                      )}
                      {schedule.type === ChairTypes.Surgeon && (
                        <ScheduleItem
                          label="Papildu asistents"
                          value={schedule.extraAssistant?.name}
                          icon={<FontAwesomeIcon icon={faUser} />}
                        />
                      )}
                      {schedule.type === ChairTypes.Hygienist && (
                        <ScheduleItem
                          label="Higiēnists"
                          value={schedule.hygenist?.name}
                          icon={<FontAwesomeIcon icon={faUser} />}
                        />
                      )}
                      {schedule.type === ChairTypes.Sanitar && (
                        <ScheduleItem
                          label="Sanitārs"
                          value={schedule.sanitar?.name}
                          icon={<FontAwesomeIcon icon={faUser} />}
                        />
                      )}
                      {schedule.type === ChairTypes.Bus && (
                        <ScheduleItem
                          label="Speciālists"
                          value={schedule.specialist?.name}
                          icon={<FontAwesomeIcon icon={faUser} />}
                        />
                      )}
                      {schedule.type === ChairTypes.Bus && (
                        <ScheduleItem
                          label="Asistents"
                          value={schedule.assistant?.name}
                          icon={<FontAwesomeIcon icon={faUser} />}
                        />
                      )}
                      {schedule.type === ChairTypes.Bus && (
                        <ScheduleItem
                          label="Šoferis"
                          value={schedule.driver?.name}
                          icon={<FontAwesomeIcon icon={faUser} />}
                        />
                      )}
                      {schedule.type === ChairTypes.Bus && (
                        <ScheduleItem
                          label="Galamērķis"
                          value={schedule.destination}
                          icon={<FontAwesomeIcon icon={faLocationDot} />}
                        />
                      )}
                    </Stack>
                  </Stack>
                </CardActionArea>
              </Tooltip>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default Schedule;
